import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Chip,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add,
  MoreVert,
  Edit,
  Delete,
  Refresh
} from '@mui/icons-material';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import { User, UserCreate, UserUpdate } from '../types';
import LoadingButton from '../components/LoadingButton';

const UsersPage: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Create user dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createData, setCreateData] = useState<UserCreate>({
    email: '',
    reseller_name: '',
    password: '',
    role: 'tenant_admin',
    is_active: true,
    max_tenants: 5
  });

  // Edit user dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editData, setEditData] = useState<UserUpdate>({});
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // Action menu
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const loadUsers = async () => {
    try {
      setError('');
      const usersData = await apiService.getUsers();
      setUsers(usersData);
    } catch (err: any) {
      console.error('Failed to load users:', err);
      setError(err.detail || 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      setCreateLoading(true);
      await apiService.register(createData);
      toast.success('Reseller created successfully');
      setCreateDialogOpen(false);
      setCreateData({ email: '', reseller_name: '', password: '', role: 'tenant_admin', is_active: true, max_tenants: 5 });
      loadUsers();
    } catch (err: any) {
      console.error('Failed to create user:', err);
      toast.error(err.detail || 'Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditUser = async () => {
    if (!editingUser) return;

    try {
      setEditLoading(true);
      await apiService.updateUser(editingUser.id, editData);
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      setEditData({});
      setEditingUser(null);
      loadUsers();
    } catch (err: any) {
      console.error('Failed to update user:', err);
      toast.error(err.detail || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (!window.confirm(`Are you sure you want to delete user "${user.email}"?`)) {
      return;
    }

    try {
      await apiService.deleteUser(user.id);
      toast.success('User deleted successfully');
      loadUsers();
    } catch (err: any) {
      console.error('Failed to delete user:', err);
      toast.error(err.detail || 'Failed to delete user');
    }
  };

  const handleActionClick = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedUser(user);
  };

  const handleActionClose = () => {
    setActionMenuAnchor(null);
    setSelectedUser(null);
  };

  const openEditDialog = (user: User) => {
    setEditingUser(user);
    setEditData({
      email: user.email,
      role: user.role,
      is_active: user.is_active
    });
    setEditDialogOpen(true);
    handleActionClose();
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  useEffect(() => {
    loadUsers();
  }, []);

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('resellers.resellers')}
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => loadUsers()}
          >
            {t('common.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
          >
            {t('resellers.createReseller')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Users Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              {t('resellers.loadingResellers')}
            </Box>
          ) : users.length === 0 ? (
            <Box py={4} textAlign="center">
              <Typography color="text.secondary">
                {t('resellers.noResellers')}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('resellers.resellerName')}</TableCell>
                    <TableCell>{t('resellers.email')}</TableCell>
                    <TableCell>{t('resellers.roleField')}</TableCell>
                    <TableCell>{t('resellers.statusField')}</TableCell>
                    <TableCell>{t('resellers.maxTenants')}</TableCell>
                    <TableCell>{t('resellers.tenantsField')}</TableCell>
                    <TableCell>{t('resellers.created')}</TableCell>
                    <TableCell>{t('tenant.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {user.reseller_name || 'Not specified'}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {user.email}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Chip 
                          label={user.role === 'super_admin' ? 'Super Admin' : 'Tenant Admin'}
                          size="small"
                          color={user.role === 'super_admin' ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      
                      <TableCell>
                        <Chip 
                          label={user.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={user.is_active ? 'success' : 'error'}
                          variant="outlined"
                        />
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {user.max_tenants || 5}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2">
                          {user.tenant_count || 0}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {format(new Date(user.created_at), 'MMM dd, yyyy')}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => handleActionClick(e, user)}
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionClose}
      >
        <MenuItem onClick={() => {
          if (selectedUser) openEditDialog(selectedUser);
        }}>
          <ListItemIcon><Edit /></ListItemIcon>
          <ListItemText>{t('common.edit')}</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => {
          if (selectedUser) handleDeleteUser(selectedUser);
          handleActionClose();
        }}>
          <ListItemIcon><Delete /></ListItemIcon>
          <ListItemText>{t('common.delete')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('resellers.createReseller')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('resellers.resellerName')}
            fullWidth
            variant="outlined"
            value={createData.reseller_name}
            onChange={(e) => setCreateData({ ...createData, reseller_name: e.target.value })}
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            label={t('resellers.email')}
            type="email"
            fullWidth
            variant="outlined"
            value={createData.email}
            onChange={(e) => setCreateData({ ...createData, email: e.target.value })}
            error={createData.email !== '' && !isValidEmail(createData.email)}
            helperText={createData.email !== '' && !isValidEmail(createData.email) ? "Please enter a valid email" : ""}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            variant="outlined"
            value={createData.password}
            onChange={(e) => setCreateData({ ...createData, password: e.target.value })}
            error={createData.password !== '' && createData.password.length < 8}
            helperText={createData.password !== '' && createData.password.length < 8 ? "Password must be at least 8 characters" : ""}
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            label={t('resellers.maxTenants')}
            type="number"
            fullWidth
            variant="outlined"
            value={createData.max_tenants}
            onChange={(e) => setCreateData({ ...createData, max_tenants: parseInt(e.target.value) || 1 })}
            inputProps={{ min: 1, max: 100 }}
            sx={{ mb: 2 }}
          />
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={createData.role}
              label="Role"
              onChange={(e) => setCreateData({ ...createData, role: e.target.value as 'super_admin' | 'tenant_admin' })}
            >
              <MenuItem value="tenant_admin">Tenant Admin</MenuItem>
              <MenuItem value="super_admin">Super Admin</MenuItem>
            </Select>
          </FormControl>
          
          <FormControlLabel
            control={
              <Switch
                checked={createData.is_active}
                onChange={(e) => setCreateData({ ...createData, is_active: e.target.checked })}
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            onClick={handleCreateUser}
            loading={createLoading}
            disabled={!createData.email || !createData.reseller_name || !createData.password || !isValidEmail(createData.email) || createData.password.length < 8}
            variant="contained"
          >
            {t('resellers.createReseller')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={editData.email || ''}
            onChange={(e) => setEditData({ ...editData, email: e.target.value })}
            error={editData.email !== '' && editData.email !== undefined && !isValidEmail(editData.email)}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="New Password (leave empty to keep current)"
            type="password"
            fullWidth
            variant="outlined"
            value={editData.password || ''}
            onChange={(e) => setEditData({ ...editData, password: e.target.value })}
            error={editData.password !== '' && editData.password !== undefined && editData.password.length < 8}
            helperText={editData.password !== '' && editData.password !== undefined && editData.password.length < 8 ? "Password must be at least 8 characters" : ""}
            sx={{ mb: 2 }}
          />
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={editData.role || ''}
              label="Role"
              onChange={(e) => setEditData({ ...editData, role: e.target.value as 'super_admin' | 'tenant_admin' })}
            >
              <MenuItem value="tenant_admin">Tenant Admin</MenuItem>
              <MenuItem value="super_admin">Super Admin</MenuItem>
            </Select>
          </FormControl>
          
          <FormControlLabel
            control={
              <Switch
                checked={editData.is_active !== undefined ? editData.is_active : true}
                onChange={(e) => setEditData({ ...editData, is_active: e.target.checked })}
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            onClick={handleEditUser}
            loading={editLoading}
            variant="contained"
          >
            Update User
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersPage;